import { Box, Text } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import type { $Keys } from 'utility-types'

import { observer } from 'mobx-react'

import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import Compensation from 'modules/contracts/store/models/Compensation'

type ExplanationIndexes = 'recurrence' | 'amount' | 'currency'

type ExplanationParams = Partial<Record<ExplanationIndexes, string>>

type CompensationTypes = $Keys<typeof Compensation.types>

interface Props {
  form: Form
  employeeLegalEntityCurrency: string
}

const Explanation = ({ form, employeeLegalEntityCurrency }: Props) => {
  const buildExplanationData = (form: Form, employeeLeCurrency: string): ExplanationParams => {
    if (form.get('compensation_type').value === Compensation.types.undefined) {
      return {
        recurrence: form.get('recurrence').value.replaceAll('_', ' '),
      }
    }

    return {
      amount: form.get('amount').value,
      currency: form.get('unit').value === 'money' ? employeeLeCurrency : form.get('unit').value,
      recurrence: form.get('recurrence').value.replaceAll('_', ' '),
    }
  }

  const dynamicTranslation = (form: Form, employeeLegalEntityCurrency: string) => {
    const compensationType: CompensationTypes = form.get('compensation_type').value
    const explanationData = buildExplanationData(form, employeeLegalEntityCurrency)

    if (hasFeature('payroll_bundle') && form.get('sync_with_supplements').value) {
      switch (compensationType) {
        case Compensation.types.fixed:
          return form.get('recurrence').value === ''
            ? i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.with_payroll.fixed.without_recurrence',
                explanationData
              )
            : i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.with_payroll.fixed.with_recurrence',
                explanationData
              )
        case Compensation.types.up_to:
          return form.get('recurrence').value === ''
            ? i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.with_payroll.up_to.without_recurrence',
                explanationData
              )
            : i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.with_payroll.up_to.with_recurrence',
                explanationData
              )
        case Compensation.types.undefined:
          return form.get('recurrence').value === ''
            ? i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.with_payroll.undefined.without_recurrence',
                explanationData
              )
            : i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.with_payroll.undefined.with_recurrence',
                explanationData
              )
        case Compensation.types.per_worked_day:
          return i18n.t(
            'contract_versions.forms.additional_compensation.modal.explanation.with_payroll.per_worked_day',
            explanationData
          )
        default:
          throw new Error(`Missing translation for ${compensationType}`)
      }
    } else {
      switch (compensationType) {
        case Compensation.types.fixed:
          return form.get('recurrence').value === ''
            ? i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.without_payroll.fixed.without_recurrence',
                explanationData
              )
            : i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.without_payroll.fixed.with_recurrence',
                explanationData
              )
        case Compensation.types.up_to:
          return form.get('recurrence').value === ''
            ? i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.without_payroll.up_to.without_recurrence',
                explanationData
              )
            : i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.without_payroll.up_to.with_recurrence',
                explanationData
              )
        case Compensation.types.undefined:
          return form.get('recurrence').value === ''
            ? i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.without_payroll.undefined.without_recurrence',
                explanationData
              )
            : i18n.t(
                'contract_versions.forms.additional_compensation.modal.explanation.without_payroll.undefined.with_recurrence',
                explanationData
              )
        case Compensation.types.per_worked_day:
          return i18n.t(
            'contract_versions.forms.additional_compensation.modal.explanation.without_payroll.per_worked_day',
            explanationData
          )
        default:
          throw new Error(`Missing translation for ${compensationType}`)
      }
    }
  }

  return (
    <Box
      borderRadius={{
        all: 'abs008',
      }}
      background="champagne400"
      paddingX="s24"
      paddingY="s16"
    >
      <Text.Normal color="grey1000" size="300">
        {dynamicTranslation(form, employeeLegalEntityCurrency)}
      </Text.Normal>
    </Box>
  )
}

export default observer(Explanation)
